import { useEffect, useRef, useState } from 'react';
import ClientController from '@/services/ClientController';
import useDebounce from '@/utils/useDebounce';
import { transformPhoneSoft } from '@/utils/funcs';

export const useClientSearch = ({ user, activeOrders, setActiveOrders }) => {
	const [clientSearchText, setClientSearchText] = useState('');
	const debouncedClientSearchText = useDebounce(clientSearchText, 1000);

	const [foundClients, setFoundClients] = useState([]);
	const [isSearchingClient, setIsSearchingClient] = useState(false);

	const [selectedClient, setSelectedClient] = useState(null);
	const [selectedAddressIndex, setSelectedAddressIndex] = useState(null);

	const [editSelectedClient, setEditSelectedClient] = useState(null);
	const [isClientModalOpen, setIsClientModalOpen] = useState(false);
	const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);

	const dropdownRef = useRef();

	useEffect(() => {
		const lengthOk =
			debouncedClientSearchText?.length >=
			(debouncedClientSearchText?.startsWith?.('+30')
				? 10
				: debouncedClientSearchText?.startsWith?.('30')
					? 9
					: 7);
		if (!lengthOk) {
			setFoundClients([]);
			setIsSearchingClient(false);
			return;
		}

		setIsSearchingClient(true);
		ClientController.searchClients({
			cityId: user?.cityLocation?.id,
			searchText: transformPhoneSoft(debouncedClientSearchText),
		}).then(res => {
			let foundClients;
			if (!res?.clients?.length > 0) {
				setFoundClients([]);
				setIsSearchingClient(false);
			} else {
				foundClients = res?.clients?.map(c => ({
					...c,
					label: c.f,
					value: c.id,
				}));

				setFoundClients(foundClients || []);
				if (foundClients) {
					if (dropdownRef?.current?.parentToggleModal) {
						dropdownRef.current.parentToggleModal(true);
					}
				}
			}

			setIsSearchingClient(false);
		});
	}, [debouncedClientSearchText]);

	const onClientSelect = (clientId, newClient) => {
		if (newClient) {
			setSelectedClient(newClient);

			if (newClient.a.length === 1) {
				setSelectedAddressIndex(0);
			}
			return;
		}

		const client = foundClients.find(it => it.id === clientId);
		if (!client) return;

		setSelectedClient(client);
		setFoundClients([]);

		if (client.a.length === 1) {
			setSelectedAddressIndex(0);
		}

		setClientSearchText('');
		setIsSearchingClient(false);
		dropdownRef.current.parentToggleModal(false);
	};

	const onFocusClient = () => {
		if (foundClients.length > 0) {
			dropdownRef.current.parentToggleModal(true, true);
		}
	};

	const onRemoveClient = () => {
		setSelectedClient(null);
		setFoundClients([]);
		setClientSearchText('');
	};

	const openClientModal = () => {
		setSelectedAddressIndex(null);
		setIsClientModalOpen(true);
	};

	const openEditClientModal = (client, clientId) => {
		if (client) {
			setEditSelectedClient({ ...client, id: clientId });

			setIsEditClientModalOpen(true);
		}
	};

	const onCloseClientModal = () => setIsClientModalOpen(false);
	const onCloseEditClientModal = () => setIsEditClientModalOpen(false);

	const onNewOrEditClient = async (clientId, newClient) => {
		if (newClient) {
			setSelectedClient(newClient);

			if (newClient.a.length === 1) {
				setSelectedAddressIndex(newClient.a.length === 1 ? 0 : null);
			}
			setFoundClients([]);
		}
	};

	const onSuccessClientEditCb = (client, clientId) => {
		if (!client) {
			return;
		}

		try {
			const newActiveOrders = [...activeOrders];
			const idxes = [];
			for (let i = 0; i < newActiveOrders.length; i++) {
				if (newActiveOrders[i].clientId === clientId) {
					idxes.push(i);
				}
			}

			for (const idx of idxes) {
				newActiveOrders[idx].client = client;
			}

			setActiveOrders(newActiveOrders);
		} catch (err) {
			console.log('onSuccessClientEditCb err:', err);
		}
	};

	return {
		clientSearchText,
		setClientSearchText,
		foundClients,
		setFoundClients,
		isSearchingClient,
		selectedClient,
		setSelectedClient,
		selectedAddressIndex,
		setSelectedAddressIndex,
		editSelectedClient,
		isClientModalOpen,
		isEditClientModalOpen,
		dropdownRef,
		onClientSelect,
		onFocusClient,
		onRemoveClient,
		openClientModal,
		openEditClientModal,
		onCloseClientModal,
		onCloseEditClientModal,
		onNewOrEditClient,
		onSuccessClientEditCb,
	};
};
